import { isNullOrEmpty } from './StringHelper'

/**
 * How the portal reads the carrier status stored on a spare-part line.
 *
 * The values arrive from Trackingmore's delivery_status field and are written through unchanged,
 * so they are lowercase (transit, pickup, delivered, notfound and the rest), but some routes store
 * them capitalised. Every comparison goes through `normalize` for that reason.
 *
 * The per-status colour and label of the old T. Status chip lived here too; IS2-177 replaced that
 * chip with the grouped Shipment Status (see sparePartSaleLineAvailability.js), so only the
 * predicates remain.
 */
const normalize = (status) =>
	isNullOrEmpty(status)
		? ''
		: String(status)
				.trim()
				.toLowerCase()

/**
 * Whether a line item has a tracking number, and therefore a shipment to report on.
 *
 * A row with no tracking number never shipped, whatever carrier status it may still carry.
 *
 * @param {object} item the spare-part line item
 * @returns {boolean} true when the row carries a tracking number
 */
export const hasTrackingStatus = (item) => !isNullOrEmpty(item && item.NumberTracking)

/**
 * Whether a carrier status means the shipment was delivered.
 *
 * Lives here rather than as a comparison at the call site because the spellings Trackingmore
 * sends are this module's business, and a status compared loose somewhere else is a status that
 * stops matching the day the carrier capitalises it differently.
 *
 * It matters beyond presentation. Once a shipment is delivered the backend deregisters it from
 * Trackingmore, so from that moment the lookup for its number answers an error -- which is the
 * ordinary end of the cycle and not a failure. Callers use this to tell the two apart.
 *
 * @param {string} status the carrier status as stored on the line item
 * @returns {boolean} true when the status reports a delivered shipment
 */
export const isDeliveredStatus = (status) => normalize(status) === 'delivered'
